import { addWeeks, endOfWeek, format, isWithinInterval, isTomorrow, startOfDay, startOfWeek } from "date-fns";
import { User } from "features/User/models";
import { isInPast } from "helpers/time";
import { sortDateTime } from "helpers/sort";
import { startCase } from "lodash";
import { UserRoleOrEmailOption } from "components/FormElements/Fields/SelectFields/UserRoleOrEmailMultiSelect";
import { userRoleEmailId } from "helpers/string";
import { ActionSummary } from "features/Resources/Shared/models";
import { Meeting, MeetingAttendee } from "./models";
import { INACTIVE_MEETING_STATUSES } from "./constants";

export function isLead(meeting: Meeting, user: User) {
  return user?.id === meeting?.leadId;
}

export function isAttendee(meeting: Meeting, user: User) {
  if (!meeting?.attendeeIds) {
    return false;
  }
  return meeting.attendeeIds.includes(user?.id);
}

export function meetingScheduledDate(action: Meeting) {
  return action?.scheduledAt ?? action?.proposedAt;
}

export function isMeetingInPast(task: Meeting) {
  return isInPast(task?.scheduledAt);
}

export function isMeetingInFuture(task: Meeting) {
  return !isInPast(task.scheduledAt);
}

export function filterMeetingsByText(meetings: Meeting[] | undefined, searchText: string | "") {
  if (!meetings) return [];
  if ((searchText || "").length < 3) {
    return meetings;
  }

  const text = searchText.toLowerCase();
  return meetings.filter(
    (meeting) =>
      meeting?.name?.toLowerCase()?.includes(text) ||
      meeting?.status?.toLowerCase()?.includes(text) ||
      meeting?.additionalInfo?.toLowerCase().includes(text) ||
      meeting?.purpose?.toLowerCase().includes(text) ||
      meeting?.learningObjective?.toLowerCase().includes(text),
  );
}

export function sortMeetings(meetings: Array<Meeting>, direction: "asc" | "desc" = "asc"): Array<Meeting> {
  return sortDateTime(meetings, (action) => meetingScheduledDate(action), direction);
}

export function attendeeToUserRoleOrEmailId(meetingAttendee: MeetingAttendee) {
  return userRoleEmailId(meetingAttendee?.emailAddress, "User", meetingAttendee?.userId, meetingAttendee?.emailType);
}

export function toUserRoleOrEmailOption(meetingAttendee: MeetingAttendee): UserRoleOrEmailOption {
  const recordId = meetingAttendee?.userId;
  let label = meetingAttendee?.emailAddress;
  let name = meetingAttendee?.emailAddress;
  if (meetingAttendee?.user) {
    name = meetingAttendee?.user?.fullName;
    if (meetingAttendee?.emailType === "personal") {
      label = `${startCase(name)} (${meetingAttendee?.emailType})`;
    } else {
      label = startCase(name);
    }
  }
  return {
    recordType: meetingAttendee?.emailAddress ? "CustomEmail" : "User",
    recordId,
    id: attendeeToUserRoleOrEmailId(meetingAttendee),
    label: label,
    email: meetingAttendee?.emailAddress,
    name: name,
    emailType: meetingAttendee?.emailType,
  };
}

export function fromUserRoleOrEmailOption(userRoleOrEmailOption: UserRoleOrEmailOption) {
  const user = {} as Partial<User>;
  if (userRoleOrEmailOption.recordType === "User") {
    user.fullName = userRoleOrEmailOption?.name;
  }
  const base = {
    userId: userRoleOrEmailOption?.email ? undefined : userRoleOrEmailOption?.recordId,
    emailAddress: userRoleOrEmailOption?.email,
    emailType: userRoleOrEmailOption?.emailType,
  };
  if (Object.keys(user).length > 0) {
    return {
      ...base,
      user,
    };
  }
  return base;
}

export function meetingTimingDescriptor(meeting: Meeting) {
  return "";
}

/** Group an array of meetings into date buckets, sorted chronologically. */
export function groupMeetingsByDate(meetings: Meeting[]): Array<{ key: string; date: Date; meetings: Meeting[] }> {
  const map = new Map<string, { date: Date; meetings: Meeting[] }>();
  for (const m of meetings) {
    const d = new Date(m.startTime);
    const key = format(d, "yyyy-MM-dd");
    if (!map.has(key)) map.set(key, { date: d, meetings: [] });
    map.get(key)!.meetings.push(m);
  }
  return Array.from(map.entries()).map(([key, { date, meetings: ms }]) => ({ key, date, meetings: ms }));
}

/** Human-readable date label for upcoming schedule groups ("Tomorrow · Mon, Jun 5", "Next week · …"). */
export function upcomingDateLabel(date: Date): string {
  if (isTomorrow(date)) return `Tomorrow · ${format(date, "EEE, MMM d")}`;
  const now = new Date();
  const nextWeekStart = addWeeks(startOfWeek(now, { weekStartsOn: 1 }), 1);
  const nextWeekEnd = endOfWeek(nextWeekStart, { weekStartsOn: 1 });
  if (isWithinInterval(startOfDay(date), { start: nextWeekStart, end: nextWeekEnd })) {
    return `Next week · ${format(date, "EEE, MMM d")}`;
  }
  return format(date, "EEE, MMM d");
}

/** Returns a short label for the other attendees visible on a meeting row ("Alice & Bob", "Alice +2 others"). */
export function getAttendeeDisplayLabel(meeting: Meeting, currentUserId?: string): string | null {
  const names = (meeting.attendees || [])
    .filter((a) => a.userId !== currentUserId)
    .map((a) => a.user?.fullName)
    .filter((n): n is string => !!n);
  if (names.length === 0) return null;
  if (names.length <= 2) return names.join(" & ");
  return `${names[0]} +${names.length - 1} others`;
}

/** Get upcoming meetings sorted by date, excluding inactive statuses. */
export function getUpcomingMeetings(actions: ActionSummary[] | undefined, limit?: number): ActionSummary[] {
  if (!actions) return [];

  const sorted = actions
    .filter((a) => a.actionType === "meeting")
    .filter((a) => !INACTIVE_MEETING_STATUSES.includes(a.action.status))
    .sort((a, b) => {
      const dateA = a.unifiedDate ? new Date(a.unifiedDate).getTime() : Infinity;
      const dateB = b.unifiedDate ? new Date(b.unifiedDate).getTime() : Infinity;
      return dateA - dateB;
    });

  return limit ? sorted.slice(0, limit) : sorted;
}
